// Direct port of the in-browser aggregation block from
// Nexus.IntegrationsWorker/Development/Utilities/sync_health_report.py.
//
// Functions here are pure: they take (DATA, state) and return numbers.
// Memoize at the React layer.

import { FAULT_NAMES } from './format';

// Run tuple: [clientIdx, providerIdx, ok(0|1), faultCode]
// 0=success, 1=ours, 2=theirs, 3=shared, 4=unknown
export function effectiveOk(r, faultMode) {
    if (r[2] === 1) return 1;
    const fc = r.length >= 4 ? r[3] : 0;
    if (faultMode === 'all') return 0;
    if (faultMode === 'ours') return fc === 1 ? 0 : 1;
    if (faultMode === 'theirs') return fc === 2 ? 0 : 1;
    return 0;
}

export function deriveClientStats(DATA) {
    const providersUsed = new Array(DATA.clients.length).fill(0);
    const seen = DATA.clients.map(() => new Set());
    for (let di = 0; di < DATA.runs.length; di++) {
        for (const r of DATA.runs[di]) seen[r[0]].add(r[1]);
    }
    for (let i = 0; i < seen.length; i++) providersUsed[i] = seen[i].size;
    return { providersUsed, jobsPerNight: DATA.jobsPerNight, students: DATA.students };
}

export function clientWeights(DATA, state, stats) {
    const n = DATA.clients.length;
    const w = new Array(n);
    const m = state.weighting;
    if (m === 'equal') {
        w.fill(1);
        return w;
    }
    const s = stats.students, j = stats.jobsPerNight, p = stats.providersUsed;
    for (let i = 0; i < n; i++) {
        let v;
        if (m === 'students') v = s[i] || 0;
        else if (m === 'jobs') v = j[i] || 0;
        else if (m === 'providers') v = p[i] || 0;
        else if (m === 'composite') {
            const sv = Math.max(1, s[i] || 0);
            const jv = Math.max(1, j[i] || 0);
            const pv = Math.max(1, p[i] || 0);
            v = Math.cbrt(sv * jv * pv);
        } else v = 1;
        w[i] = v;
    }
    let sum = 0, count = 0;
    for (let i = 0; i < n; i++) {
        if (state.excludedClients.has(i)) continue;
        sum += w[i];
        count++;
    }
    const meanW = count ? sum / count : 1;
    if (meanW > 0) for (let i = 0; i < n; i++) w[i] = w[i] / meanW;
    return w;
}

export function meanArr(arr) {
    const clean = arr.filter((x) => x != null && !isNaN(x));
    if (!clean.length) return NaN;
    return clean.reduce((a, b) => a + b, 0) / clean.length;
}

export function rolling(arr, window) {
    const out = new Array(arr.length).fill(NaN);
    for (let i = 0; i < arr.length; i++) {
        const start = Math.max(0, i - window + 1);
        const slice = arr.slice(start, i + 1).filter((x) => x != null && !isNaN(x));
        if (slice.length >= 3) out[i] = slice.reduce((a, b) => a + b, 0) / slice.length;
    }
    return out;
}

export function linregSlope(arr) {
    const pts = [];
    for (let i = 0; i < arr.length; i++) {
        if (arr[i] != null && !isNaN(arr[i])) pts.push([i, arr[i]]);
    }
    if (pts.length < 2) return 0;
    const n = pts.length;
    let sx = 0, sy = 0, sxx = 0, sxy = 0;
    for (const [x, y] of pts) {
        sx += x; sy += y; sxx += x * x; sxy += x * y;
    }
    return (n * sxy - sx * sy) / (n * sxx - sx * sx);
}

export function lastN(arr, n) {
    return meanArr(arr.slice(Math.max(0, arr.length - n)));
}

export function priorN(arr, n) {
    const end = Math.max(0, arr.length - n);
    const start = Math.max(0, end - n);
    return meanArr(arr.slice(start, end));
}

export function aggregate(DATA, state) {
    const nDates = DATA.dates.length;
    const runRate = new Array(nDates).fill(NaN);
    const clientNightRate = new Array(nDates).fill(NaN);
    const systemClean = new Array(nDates).fill(NaN);
    const perClient = {};
    const perProvider = {};

    const stats = deriveClientStats(DATA);
    const w = clientWeights(DATA, state, stats);

    for (let di = 0; di < nDates; di++) {
        const night = DATA.runs[di];
        const filtered = night.filter(
            (r) => !state.excludedClients.has(r[0]) && !state.excludedProviders.has(r[1])
        );
        if (!filtered.length) continue;

        let weightedOk = 0, weightedTotal = 0, effOk = 0;
        for (const r of filtered) {
            const wr = w[r[0]];
            const ok = effectiveOk(r, state.faultMode);
            weightedTotal += wr;
            if (ok) {
                weightedOk += wr;
                effOk++;
            }
        }
        runRate[di] = weightedTotal > 0 ? (weightedOk / weightedTotal) * 100 : NaN;

        const byClient = new Map();
        const byProvider = new Map();
        for (const r of filtered) {
            const ok = effectiveOk(r, state.faultMode);
            if (!byClient.has(r[0])) byClient.set(r[0], { total: 0, ok: 0 });
            const c = byClient.get(r[0]); c.total++; if (ok) c.ok++;
            if (!byProvider.has(r[1])) byProvider.set(r[1], { total: 0, ok: 0 });
            const p = byProvider.get(r[1]); p.total++; if (ok) p.ok++;
        }

        let wClean = 0, wTotal = 0;
        for (const [ci, s] of byClient) {
            wTotal += w[ci];
            if (s.ok === s.total) wClean += w[ci];
            if (!perClient[ci]) {
                perClient[ci] = {
                    run: new Array(nDates).fill(NaN),
                    night: new Array(nDates).fill(NaN),
                };
            }
            perClient[ci].run[di] = (s.ok / s.total) * 100;
            perClient[ci].night[di] = s.ok === s.total ? 100 : 0;
        }
        clientNightRate[di] = wTotal > 0 ? (wClean / wTotal) * 100 : NaN;

        for (const [pi, s] of byProvider) {
            if (!perProvider[pi]) {
                perProvider[pi] = {
                    run: new Array(nDates).fill(NaN),
                    night: new Array(nDates).fill(NaN),
                };
            }
            perProvider[pi].run[di] = (s.ok / s.total) * 100;
            perProvider[pi].night[di] = s.ok === s.total ? 100 : 0;
        }

        const failedRuns = filtered.length - effOk;
        systemClean[di] = failedRuns === 0 ? 100 : 0;
    }

    return { runRate, clientNightRate, systemClean, perClient, perProvider };
}

export function faultBreakdown(DATA, state, lastNDays) {
    const out = { ours: 0, theirs: 0, shared: 0, unknown: 0, total: 0 };
    const start = Math.max(0, DATA.runs.length - lastNDays);
    for (let di = start; di < DATA.runs.length; di++) {
        for (const r of DATA.runs[di]) {
            if (state.excludedClients.has(r[0]) || state.excludedProviders.has(r[1])) continue;
            if (r[2] === 1) continue;
            const fc = r.length >= 4 ? r[3] : 4;
            const name = FAULT_NAMES[fc] || 'unknown';
            if (out[name] !== undefined) out[name]++;
            out.total++;
        }
    }
    return out;
}

export function clientFaultMix(DATA, state) {
    const byClient = {};
    for (let di = 0; di < DATA.runs.length; di++) {
        for (const r of DATA.runs[di]) {
            if (state.excludedClients.has(r[0]) || state.excludedProviders.has(r[1])) continue;
            if (r[2] === 1) continue;
            const fc = r.length >= 4 ? r[3] : 4;
            if (!byClient[r[0]]) {
                byClient[r[0]] = { ours: 0, theirs: 0, shared: 0, unknown: 0, total: 0 };
            }
            const bucket = byClient[r[0]];
            const name = FAULT_NAMES[fc] || 'unknown';
            if (bucket[name] !== undefined) bucket[name]++;
            bucket.total++;
        }
    }
    return byClient;
}

export function countVolumes(DATA, state) {
    const cVol = {}, pVol = {};
    for (let di = 0; di < DATA.runs.length; di++) {
        for (const r of DATA.runs[di]) {
            if (state.excludedClients.has(r[0]) || state.excludedProviders.has(r[1])) continue;
            cVol[r[0]] = (cVol[r[0]] || 0) + 1;
            pVol[r[1]] = (pVol[r[1]] || 0) + 1;
        }
    }
    return { cVol, pVol };
}

function argmin(arr) {
    let min = Infinity, idx = -1;
    for (let i = 0; i < arr.length; i++) {
        if (arr[i] != null && !isNaN(arr[i]) && arr[i] < min) {
            min = arr[i];
            idx = i;
        }
    }
    return idx;
}

export function buildRanking(perEntity, names, RECENT) {
    const rows = [];
    for (const idx in perEntity) {
        const run = perEntity[idx].run;
        const night = perEntity[idx].night;
        const nightsRan = run.filter((x) => x != null && !isNaN(x)).length;
        rows.push({
            idx: +idx,
            name: names[idx],
            last14_run: lastN(run, RECENT),
            prior14_run: priorN(run, RECENT),
            last14_clean: lastN(night, RECENT),
            prior14_clean: priorN(night, RECENT),
            full_run: meanArr(run),
            full_clean: meanArr(night),
            worst_run: Math.min.apply(
                null,
                run.filter((x) => x != null && !isNaN(x)).concat([100])
            ),
            worst_run_idx: argmin(run),
            nightsRan,
            volume: 0,
        });
    }
    for (const r of rows) {
        r.delta_run =
            isNaN(r.last14_run) || isNaN(r.prior14_run)
                ? NaN
                : r.last14_run - r.prior14_run;
        r.delta_clean =
            isNaN(r.last14_clean) || isNaN(r.prior14_clean)
                ? NaN
                : r.last14_clean - r.prior14_clean;
    }
    return rows;
}

export function sortBy(rows, key, asc) {
    return rows.slice().sort((a, b) => {
        const av = a[key], bv = b[key];
        if (isNaN(av) && isNaN(bv)) return 0;
        if (isNaN(av)) return 1;
        if (isNaN(bv)) return -1;
        return asc ? av - bv : bv - av;
    });
}
